import { ChevronRight, ChevronDown } from "lucide-react";
import type { ThemaCode } from "reedy-data/api";
import { TableCell, TableRow } from "~/components/table";
import { cn } from "~/utils";
import { SelectToggleIcon } from "~/components/select-toggle-icon";
import { InfoTooltip } from "~/components/info-tooltip";
import { Text } from "~/components/text";
import { ThemaCodesTableRowHighlightedText } from "./thema-codes-table-row-highlighted-text";

export const ThemaCodesTableRow = ({
  item,
  selected = false,
  onToggle,
  onDrillDown,
  isExpanded = false,
  showNotesInline = false,
  flatLayout = false,
  renderAction,
}: {
  item: ThemaCode;
  selected: boolean;
  onToggle?: () => void;
  onDrillDown: () => void;
  isExpanded?: boolean;
  showNotesInline?: boolean;
  flatLayout?: boolean;
  renderAction?: (item: ThemaCode) => React.ReactNode;
}) => {
  // Remove indentation in flat layout mode since results are flattened
  const indentValue = flatLayout ? 0 : (item.level ?? 0);
  const indentStyle = { paddingLeft: `${(indentValue + 1) * 16}px` };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger drill down if clicking on the select toggle
    if ((e.target as HTMLElement).closest("[data-select-toggle]")) {
      return;
    }

    // Don't allow drill down in flat layout mode
    if (item.has_children && !flatLayout) {
      onDrillDown();
    }
  };

  // Don't show chevron icons in flat layout mode
  const shouldShowChevron = item.has_children && !flatLayout;

  // Only allow click interaction when not in flat layout mode and item has children
  const shouldAllowClick = !flatLayout && item.has_children;

  const renderCell = () => {
    if (renderAction) return renderAction(item);

    if (onToggle) {
      return (
        <div data-select-toggle>
          <SelectToggleIcon
            selected={selected}
            id={item.id}
            onClick={onToggle}
            className={cn(
              !selected && "opacity-0",
              "text-primary-800 group-hover:opacity-100 transition-opacity group-hover:duration-200 duration-0",
            )}
            size="small"
          />
        </div>
      );
    }

    return null;
  };

  return (
    <TableRow
      className={cn(
        "hover:bg-secondary-25 text-sm group",
        selected && "bg-primary-50 hover:bg-primary-50",
        isExpanded && "bg-primary-25 font-medium",
      )}
      onClick={shouldAllowClick ? handleRowClick : undefined}
      data-testid={`thema-row-${item.code}`}
    >
      <TableCell className="font-mono font-medium py-2" style={indentStyle}>
        <ThemaCodesTableRowHighlightedText
          text={item.highlighted_code}
          fallback={item.code}
          uppercase
        />
      </TableCell>
      <TableCell className="text-secondary-700 py-2 whitespace-normal">
        <div className="flex items-start w-full">
          <div className="flex-1 min-w-0">
            <div
              className="break-words hyphens-auto"
              style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
            >
              <div className="flex items-center">
                <span className="flex min-w-0 mr-4">
                  <ThemaCodesTableRowHighlightedText
                    text={item.highlighted_description}
                    fallback={item.description}
                  />
                </span>
                {item.notes && !showNotesInline && (
                  <InfoTooltip
                    text={
                      <ThemaCodesTableRowHighlightedText
                        text={item.highlighted_notes}
                        fallback={item.notes}
                      />
                    }
                    iconSize="small"
                    side="bottom"
                    contentClassName="max-w-sm"
                  />
                )}
                {shouldShowChevron && (
                  <span
                    className={cn(
                      "inline-flex items-center justify-center w-5 h-5 rounded hover:bg-secondary-100 transition-opacity flex-shrink-0",
                    )}
                    data-testid={`drill-down-icon-${item.code}`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3 text-secondary-500" />
                    ) : (
                      <ChevronRight className="w-3 h-3 text-secondary-500" />
                    )}
                  </span>
                )}
              </div>
            </div>
            {item.notes && showNotesInline && (
              <Text
                as="div"
                variant="captionItalic"
                className="mt-1 break-words hyphens-auto"
              >
                <ThemaCodesTableRowHighlightedText
                  text={item.highlighted_notes}
                  fallback={item.notes}
                />
              </Text>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-right w-12 py-2 px-4">
        {renderCell()}
      </TableCell>
    </TableRow>
  );
};
